import { useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectBeneficiariesWorkspaceApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { CommentThread } from '../../../components/CommentThread';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectBeneficiaries } from '../../../types/projects';

type CategoryPrefix = 'idp' | 'refugees' | 'returnees' | 'host' | 'other';
type FieldKey = keyof ProjectBeneficiaries;

const CATEGORIES: { title: string; prefix: CategoryPrefix; accent: string }[] = [
  { title: 'Internally Displaced People', prefix: 'idp', accent: 'outcome-accent-1' },
  { title: 'Refugees', prefix: 'refugees', accent: 'outcome-accent-2' },
  { title: 'Returnees', prefix: 'returnees', accent: 'outcome-accent-3' },
  { title: 'Host Communities', prefix: 'host', accent: 'outcome-accent-4' },
  { title: 'Non-Displacement-Related (Other)', prefix: 'other', accent: 'outcome-accent-5' },
];

function num(v: number | string | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Structure matches the OCHA reference exactly: displacement
 * categories (IDP/Refugees/Returnees/Host Communities/Non-
 * Displacement-Related), each broken into Women(>=18)/Girls(<18)/
 * Men(>=18)/Boys(<18), with a Persons with Disabilities row
 * calculated separately. Every total shown here is computed, never
 * entered - Section 10's explicit requirement.
 */
export function ProjectBeneficiariesTab() {
  const { project } = useOutletContext<{ project: Project }>();
  const canManage = usePermission('project_beneficiaries.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ['project-beneficiaries', project.id], queryFn: () => projectBeneficiariesWorkspaceApi.get(project.id) });
  const { register, handleSubmit, watch } = useForm<Partial<ProjectBeneficiaries>>({ values: query.data?.data });

  const saveMutation = useMutation({
    mutationFn: (values: Partial<ProjectBeneficiaries>) => projectBeneficiariesWorkspaceApi.update(project.id, values),
    onSuccess: (result) => { showSuccess('Beneficiary information saved.'); queryClient.setQueryData(['project-beneficiaries', project.id], { data: result.data }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  if (query.isLoading) return <p className="muted">Loading…</p>;

  const values = watch();

  // Grand totals across every displacement category, per gender/age column.
  let grandWomen = 0, grandGirls = 0, grandMen = 0, grandBoys = 0;
  const categoryTotals = CATEGORIES.map((cat) => {
    const women = num(values[`${cat.prefix}_women` as FieldKey]);
    const girls = num(values[`${cat.prefix}_girls` as FieldKey]);
    const men = num(values[`${cat.prefix}_men` as FieldKey]);
    const boys = num(values[`${cat.prefix}_boys` as FieldKey]);
    grandWomen += women; grandGirls += girls; grandMen += men; grandBoys += boys;
    return { ...cat, women, girls, men, boys, total: women + girls + men + boys };
  });
  const grandTotal = grandWomen + grandGirls + grandMen + grandBoys;
  const pctFemale = grandTotal > 0 ? Math.round(((grandWomen + grandGirls) / grandTotal) * 100) : 0;
  const pctMale = grandTotal > 0 ? Math.round(((grandMen + grandBoys) / grandTotal) * 100) : 0;
  const pctChild = grandTotal > 0 ? Math.round(((grandGirls + grandBoys) / grandTotal) * 100) : 0;

  const pwdWomen = num(values.pwd_women);
  const pwdGirls = num(values.pwd_girls);
  const pwdMen = num(values.pwd_men);
  const pwdBoys = num(values.pwd_boys);
  const pwdTotal = pwdWomen + pwdGirls + pwdMen + pwdBoys;
  const pwdPct = grandTotal > 0 ? Math.round((pwdTotal / grandTotal) * 100) : 0;

  return (
    <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))}>
      <section className="card">
        <h2>Beneficiary Targets</h2>
        <div className="beneficiary-grid">
          <label>Direct Beneficiaries (Target)<input type="number" min="0" {...register('target_direct')} disabled={!canManage} /></label>
          <label>Indirect Beneficiaries (Target)<input type="number" min="0" {...register('target_indirect')} disabled={!canManage} /></label>
        </div>
      </section>

      {categoryTotals.map((cat) => (
        <section key={cat.prefix} className={`card ${cat.accent}`}>
          <h3>{cat.title}</h3>
          <div className="beneficiary-grid beneficiary-grid-4">
            <label>Women (&ge;18)<input type="number" min="0" {...register(`${cat.prefix}_women` as FieldKey)} disabled={!canManage} /></label>
            <label>Girls (&lt;18)<input type="number" min="0" {...register(`${cat.prefix}_girls` as FieldKey)} disabled={!canManage} /></label>
            <label>Men (&ge;18)<input type="number" min="0" {...register(`${cat.prefix}_men` as FieldKey)} disabled={!canManage} /></label>
            <label>Boys (&lt;18)<input type="number" min="0" {...register(`${cat.prefix}_boys` as FieldKey)} disabled={!canManage} /></label>
          </div>
          <p className="muted">Category Total: <strong>{cat.total.toLocaleString()}</strong></p>
        </section>
      ))}

      <section className="card beneficiary-summary-card">
        <h3>Total People Directly Targeted</h3>
        <table className="beneficiary-summary-table">
          <thead><tr><th>Women (&ge;18)</th><th>Girls (&lt;18)</th><th>Men (&ge;18)</th><th>Boys (&lt;18)</th><th>Total</th></tr></thead>
          <tbody>
            <tr>
              <td>{grandWomen.toLocaleString()}</td>
              <td>{grandGirls.toLocaleString()}</td>
              <td>{grandMen.toLocaleString()}</td>
              <td>{grandBoys.toLocaleString()}</td>
              <td><strong>{grandTotal.toLocaleString()}</strong></td>
            </tr>
          </tbody>
        </table>
        <p className="muted beneficiary-pct-row">{pctFemale}% Female &nbsp;·&nbsp; {pctMale}% Male &nbsp;·&nbsp; {pctChild}% Child</p>
      </section>

      <section className="card outcome-accent-4">
        <h3>Persons with Disabilities</h3>
        <div className="beneficiary-grid beneficiary-grid-4">
          <label>Women (&ge;18)<input type="number" min="0" {...register('pwd_women')} disabled={!canManage} /></label>
          <label>Girls (&lt;18)<input type="number" min="0" {...register('pwd_girls')} disabled={!canManage} /></label>
          <label>Men (&ge;18)<input type="number" min="0" {...register('pwd_men')} disabled={!canManage} /></label>
          <label>Boys (&lt;18)<input type="number" min="0" {...register('pwd_boys')} disabled={!canManage} /></label>
        </div>
        <p className="muted">Total: <strong>{pwdTotal.toLocaleString()}</strong> ({pwdPct}% of people directly targeted)</p>
      </section>

      <section className="card">
        <h3>Other Vulnerable Groups</h3>
        <label>Notes<textarea rows={2} {...register('other_vulnerable_notes')} disabled={!canManage} /></label>
        <p className="muted">Geographic breakdown of beneficiaries is recorded on the Locations tab.</p>
        <CommentThread projectId={project.id} entityType="beneficiaries" />
      </section>

      {canManage && <div className="form-actions"><button type="submit" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save Beneficiaries'}</button></div>}
    </form>
  );
}
